import { TASK_STATUS_LABELS, TASK_STATUSES, type TaskStatus } from '@crm/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { TextAlignStart } from 'lucide-react';
import { useState } from 'react';
import { api, type TaskView } from '../../lib/client.ts';
import { Board, type BoardColumn, type BoardSource } from '../boards/kanban-board.tsx';
import { tasksQuery } from '../records/record-sidebar.tsx';
import { Badge } from '../ui/badge.tsx';
import { STATUS_BADGE, TaskDetailDialog } from './task-detail-dialog.tsx';
import { TaskRecordLink } from './task-record-link.tsx';

type StatusColumn = BoardColumn & { id: TaskStatus };

const COLUMNS: readonly StatusColumn[] = TASK_STATUSES.map((s) => ({
  id: s,
  name: TASK_STATUS_LABELS[s],
  header: <Badge variant={STATUS_BADGE[s]}>{TASK_STATUS_LABELS[s]}</Badge>,
}));

/**
 * My Tasks' and Team Tasks' board (TS-1742, TS-1744): a column per status on the shared
 * kanban board, and a drag is the same status PATCH as the list's picker, so it writes the
 * same audit and timeline rows. Each column is one read of the page's own list endpoint with
 * that status (the API's 500 cap applies per column), so the board shows exactly what the list
 * shows, filters included.
 */
export function TaskBoard({
  name,
  scope,
  load,
  canMove,
  canDelete,
  showAssignee = false,
}: {
  /** `my-tasks` or `team-tasks`: query keys and test ids. */
  name: string;
  /** What `load` depends on (the page's filters). */
  scope: readonly unknown[];
  load: (status: TaskStatus) => Promise<TaskView[]>;
  canMove: (task: TaskView) => boolean;
  canDelete: (task: TaskView) => boolean;
  showAssignee?: boolean;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState<TaskView | null>(null);
  // Not memoised: the board keys its queries on `scope`, not on this object's identity.
  const source: BoardSource<TaskView, StatusColumn> = {
    scope,
    page: async (status) => ({ items: await load(status as TaskStatus), nextCursor: null }),
    move: (task, status) => api.tasks.patch(task.id, { status: status as TaskStatus }),
    idOf: (t) => t.id,
    stageOf: (t) => t.status,
    titleOf: (t) => t.title,
    moved: (t, column) => ({
      ...t,
      status: column.id,
      completedAt: column.id === 'done' ? new Date().toISOString() : null,
    }),
    canMoveItem: canMove,
  };
  // Everywhere else a task shows: the list layouts and the record it is on.
  const refreshOthers = async (task?: TaskView) => {
    await qc.invalidateQueries({ queryKey: ['tasks'] });
    if (task) await qc.invalidateQueries({ queryKey: tasksQuery(task.entityId, task.entityType) });
  };
  return (
    <>
      <Board
        name={name}
        columns={COLUMNS}
        source={source}
        canMove
        onOpen={setOpen}
        onMoved={() => refreshOthers()}
        renderCard={(t) => <TaskCard task={t} showAssignee={showAssignee} />}
      />
      {open && (
        <TaskDetailDialog
          task={open}
          canEdit={canMove(open)}
          canDelete={canDelete(open)}
          onClose={() => {
            setOpen(null);
            // The dialog refreshes the `tasks` lists; the board's columns live under `board`.
            void qc.invalidateQueries({ queryKey: ['board', name] });
          }}
        />
      )}
    </>
  );
}

function TaskCard({ task, showAssignee }: { task: TaskView; showAssignee: boolean }) {
  const done = task.status === 'done';
  const overdue = !done && task.dueAt !== null && new Date(task.dueAt).getTime() < Date.now();
  return (
    <div
      className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
      data-testid="task-card"
    >
      <TaskRecordLink task={task} />
      {task.dueAt && (
        <span className={overdue ? 'text-danger' : ''} data-testid="task-card-due">
          {overdue ? 'Overdue · ' : 'Due '}
          {new Date(task.dueAt).toLocaleDateString()}
        </span>
      )}
      {showAssignee && task.assignee && (
        <span data-testid="task-assignee">{task.assignee.name}</span>
      )}
      {task.description && <TextAlignStart className="size-3.5" aria-label="Has a description" />}
    </div>
  );
}
